"use client";

import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MailIcon, MapPinIcon, MessageCircle, PhoneIcon } from "lucide-react";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import { submitContactForm } from "@/actions/submit-contact";

function SubmitButton({ label, loadingLabel }: { label: string; loadingLabel: string }) {
    const { pending } = useFormStatus();
    return (
        <Button
            disabled={pending}
            type="submit"
            className="mt-6 w-full rounded-md from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5"
            size="lg"
        >
            {pending ? loadingLabel : label}
        </Button>
    )
}

const Contact = () => {
    const t = useTranslations("marketing.contact");
    const locale = useLocale();
    const [state, formAction] = useActionState(submitContactForm, null);

    return (
        <div className="min-h-screen flex items-center justify-center py-16">
            <div className="w-full max-w-7xl mx-auto px-6 xl:px-0">
                <b className="text-muted-foreground uppercase font-semibold text-sm">
                    {t("badge")}
                </b>
                <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight">
                    {t("title")}
                </h2>
                <p className="mt-3 text-base sm:text-lg text-muted-foreground">
                    {t("description")}
                </p>
                <div className="mt-16 flex flex-col lg:flex-row gap-16 md:gap-10">
                    {/* Contact Cards Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:col-span-2 gap-1 border border-border dark:border-white/10 p-1 bg-muted/50 dark:bg-white/5 max-w-3xl w-full">
                        {/* Email */}
                        <div className="border border-border dark:border-white/10 p-6 bg-background">
                            <div className="h-12 w-12 flex items-center justify-center rounded-md from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5">
                                <MailIcon />
                            </div>
                            <h3 className="mt-6 font-semibold text-xl">{t("email.title")}</h3>
                            <p className="my-2.5 text-muted-foreground">
                                {t("email.description")}
                            </p>
                            <Link
                                className="font-medium text-primary"
                                href="mailto:contact@ecommaps.com"
                            >
                                contact@ecommaps.com
                            </Link>
                        </div>

                        {/* Live Chat */}
                        <div className="border border-border dark:border-white/10 p-6 bg-background">
                            <div className="h-12 w-12 flex items-center justify-center rounded-md from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5">
                                <MessageCircle />
                            </div>
                            <h3 className="mt-6 font-semibold text-xl">{t("chat.title")}</h3>
                            <p className="my-2.5 text-muted-foreground">
                                {t("chat.description")}
                            </p>
                            <Link className="font-medium text-primary" href="#">
                                {t("chat.link")}
                            </Link>
                        </div>

                        {/* Office */}
                        <div className="border border-border dark:border-white/10 p-6 bg-background">
                            <div className="h-12 w-12 flex items-center justify-center rounded-md from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5">
                                <MapPinIcon />
                            </div>
                            <h3 className="mt-6 font-semibold text-xl">{t("office.title")}</h3>
                            <p className="my-2.5 text-muted-foreground">
                                {t("office.description")}
                            </p>
                            <Link
                                className="font-medium text-primary"
                                href="https://maps.google.com/?q=Algiers,Algeria"
                                target="_blank"
                            >
                                {t("office.address")}
                            </Link>
                        </div>

                        {/* Phone */}
                        <div className="border border-border dark:border-white/10 p-6 bg-background">
                            <div className="h-12 w-12 flex items-center justify-center rounded-md from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5">
                                <PhoneIcon />
                            </div>
                            <h3 className="mt-6 font-semibold text-xl">{t("phone.title")}</h3>
                            <p className="my-2.5 text-muted-foreground">
                                {t("phone.description")}
                            </p>
                            <Link
                                className="font-medium text-primary"
                                href="tel:+213550000000"
                            >
                                +213 550 000 000
                            </Link>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="border border-border dark:border-white/10 p-1 bg-muted/50 dark:bg-white/5 w-full max-w-lg">
                        <Card className="relative isolate bg-background shadow-none lg:ms-auto rounded-none border-0">
                            <CardHeader>
                                <CardTitle>{t("form.title")}</CardTitle>
                                <CardDescription>
                                    {t("form.description")}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="mt-2">
                                <form action={formAction}>
                                    <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                                        <div className="col-span-2 sm:col-span-1">
                                            <Label htmlFor="firstName">{t("form.firstName")}</Label>
                                            <Input
                                                name="firstName"
                                                placeholder={t("form.firstNamePlaceholder")}
                                                id="firstName"
                                                className="mt-2 bg-background h-10 shadow-none"
                                                required
                                            />
                                            {state?.fieldErrors?.firstName && <p className="text-red-500 text-xs mt-1">{state.fieldErrors.firstName[0]}</p>}
                                        </div>
                                        <div className="col-span-2 sm:col-span-1">
                                            <Label htmlFor="lastName">{t("form.lastName")}</Label>
                                            <Input
                                                name="lastName"
                                                placeholder={t("form.lastNamePlaceholder")}
                                                id="lastName"
                                                className="mt-2 bg-background h-10 shadow-none"
                                                required
                                            />
                                            {state?.fieldErrors?.lastName && <p className="text-red-500 text-xs mt-1">{state.fieldErrors.lastName[0]}</p>}
                                        </div>
                                        <div className="col-span-2">
                                            <Label htmlFor="email">{t("form.email")}</Label>
                                            <Input
                                                name="email"
                                                type="email"
                                                placeholder={t("form.emailPlaceholder")}
                                                id="email"
                                                className="mt-2 bg-background h-10 shadow-none"
                                                required
                                            />
                                            {state?.fieldErrors?.email && <p className="text-red-500 text-xs mt-1">{state.fieldErrors.email[0]}</p>}
                                        </div>
                                        <div className="col-span-2">
                                            <Label htmlFor="message">{t("form.message")}</Label>
                                            <Textarea
                                                name="message"
                                                id="message"
                                                placeholder={t("form.messagePlaceholder")}
                                                className="mt-2 bg-background shadow-none"
                                                rows={6}
                                                required
                                            />
                                            {state?.fieldErrors?.message && <p className="text-red-500 text-xs mt-1">{state.fieldErrors.message[0]}</p>}
                                        </div>
                                        <div className="col-span-2 flex items-center gap-2">
                                            <Checkbox name="acceptTerms" id="acceptTerms" className="bg-background" required />
                                            <Label htmlFor="acceptTerms" className="gap-0">
                                                {t("form.agree")}
                                                <Link href={`https://ecommaps.com/${locale}/legal/terms`} className="underline ms-1" target="_blank">
                                                    {t("form.terms")}
                                                </Link>
                                                <span>.</span>
                                            </Label>
                                            {state?.fieldErrors?.acceptTerms && <p className="text-red-500 text-xs mt-1 ms-2">{state.fieldErrors.acceptTerms[0]}</p>}
                                        </div>
                                    </div>

                                    {state?.error && (
                                        <div className="mt-4 p-3 bg-red-500/10 text-red-500 rounded-md text-sm border border-red-500/20">
                                            {state.error}
                                        </div>
                                    )}

                                    {state?.success ? (
                                        <div className="mt-6 p-4 bg-green-500/10 text-green-600 rounded-md border border-green-500/20 text-center">
                                            <h4 className="font-semibold mb-1">{t("form.successTitle")}</h4>
                                            <p className="text-sm">{t("form.successMessage")}</p>
                                        </div>
                                    ) : (
                                        <SubmitButton label={t("form.submit")} loadingLabel={t("form.sending")} />
                                    )}
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
